import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiArrowRight, FiCheckCircle, FiShield, FiLock, FiFileText,
  FiDownload, FiClock, FiSearch, FiList, FiLayers,
  FiWifi, FiAlertTriangle, FiUsers, FiRefreshCw,
  FiServer, FiBox, FiCpu, FiBarChart2
} = FiIcons;

export default function Landing() {
  const navigate = useNavigate();

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32 px-6">
        {/* Abstract background */}
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px]"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-400 font-bold text-xs uppercase tracking-widest mb-8 border border-blue-500/20">
                <SafeIcon icon={FiShield} />
                <span>Herramienta de Diagnóstico Gratuita</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight mb-8 leading-[1.1]">
                Diagnóstico <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-300 to-emerald-400">
                  Protege tu Red
                </span>
              </h1>
              
              <p className="text-xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Evalúa la madurez de seguridad en tu infraestructura, identifica brechas críticas y recibe un plan de acción profesional diseñado para tu negocio.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-stretch sm:items-center mb-6">
                <button 
                  onClick={() => navigate('/assessment')}
                  className="px-8 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 shadow-2xl shadow-blue-600/30 group"
                >
                  Comenzar diagnóstico
                  <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <a 
                  href="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/documents/YXBwbGljYXRpb24vcGRm/ce44d412c8700e3f13d31873f2f34c28.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-8 py-5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 border border-slate-800"
                >
                  Descargar Guía PDF
                  <SafeIcon icon={FiDownload} />
                </a>
              </div>

              <p className="text-sm text-slate-500 flex items-center justify-center lg:justify-start gap-2">
                <SafeIcon icon={FiClock} className="text-blue-500" />
                Toma aproximadamente 5–7 minutos. Recibirás tu resultado y un plan de acción personalizado al finalizar.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.9, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex justify-center lg:justify-end"
            >
              {/* Background glow adapted for the bigger image */}
              <div className="absolute w-full h-full bg-blue-600/20 blur-[100px] rounded-full scale-110 animate-pulse"></div>
              
              {/* Static, much bigger image */}
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1782499386949-image.png" 
                alt="Libro Protege tu Red" 
                className="relative z-10 w-full max-w-lg lg:max-w-[600px] xl:max-w-[700px] drop-shadow-[0_35px_35px_rgba(0,0,0,0.6)] object-contain"
              />
            </motion.div>

          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-20 bg-slate-900/30 px-6 border-y border-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div {...fadeIn} className="bg-slate-900/50 p-8 rounded-2xl border border-slate-800 hover:border-blue-500/50 transition-all group">
              <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiLock} className="text-blue-400 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Respuestas privadas</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Tus respuestas se usan para generar tu diagnóstico y reporte. No compartimos tu información con terceros.</p>
            </motion.div>
            
            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="bg-slate-900/50 p-8 rounded-2xl border border-slate-800 hover:border-emerald-500/50 transition-all group">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiCheckCircle} className="text-emerald-400 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Resultados inmediatos</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Obtén tu nivel de riesgo y puntaje general al instante tras finalizar el cuestionario, sin esperas.</p>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.4 }} className="bg-slate-900/50 p-8 rounded-2xl border border-slate-800 hover:border-purple-500/50 transition-all group">
              <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <SafeIcon icon={FiFileText} className="text-purple-400 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Plan de acción a 30 días</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Recibe una guía paso a paso basada en tus áreas más débiles para mejorar tu seguridad rápidamente.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works Section */}
      <section id="como-funciona" className="py-32 px-6 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-white mb-4">Cómo funciona</h2>
            <p className="text-slate-400">Tres pasos simples para asegurar el futuro de tu negocio.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-[60px] left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0"></div>

            <motion.div {...fadeIn} className="text-center relative z-10">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-8 shadow-xl shadow-blue-600/20 ring-8 ring-blue-600/10">1</div>
              <h3 className="text-xl font-bold text-white mb-4">Responde preguntas simples</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Contesta preguntas sobre accesos, red, respaldos, capacitación y documentación.</p>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="text-center relative z-10">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-8 shadow-xl shadow-blue-600/20 ring-8 ring-blue-600/10">2</div>
              <h3 className="text-xl font-bold text-white mb-4">Recibe tu puntuación</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Obtén un resultado claro con tu nivel de preparación y áreas de mayor riesgo detectadas.</p>
            </motion.div>

            <motion.div {...fadeIn} transition={{ delay: 0.4 }} className="text-center relative z-10">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-8 shadow-xl shadow-blue-600/20 ring-8 ring-blue-600/10">3</div>
              <h3 className="text-xl font-bold text-white mb-4">Descarga tu plan</h3>
              <p className="text-slate-400 text-sm leading-relaxed">Recibe un plan de acción de 30 días con pasos prácticos y directos para implementar hoy mismo.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What it evaluates Section */}
      <section id="que-evalua" className="py-32 px-6 bg-slate-900/20 border-y border-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-white mb-4">Qué evalúa el diagnóstico</h2>
            <p className="text-slate-400">Analizamos los 10 pilares fundamentales de la seguridad de red.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { icon: FiUsers, title: "Organización" },
              { icon: FiList, title: "Inventario" },
              { icon: FiLock, title: "Accesos" },
              { icon: FiWifi, title: "Wi-Fi y red" },
              { icon: FiSearch, title: "Detección" },
              { icon: FiCpu, title: "Capacitación" },
              { icon: FiAlertTriangle, title: "Respuesta" },
              { icon: FiServer, title: "Respaldos" },
              { icon: FiBox, title: "Evidencia" },
              { icon: FiLayers, title: "Proveedor TI" }
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                {...fadeIn}
                transition={{ delay: idx * 0.05 }}
                className="bg-slate-900 border border-slate-800 p-6 rounded-xl hover:bg-slate-800 transition-all text-center group"
              >
                <SafeIcon icon={item.icon} className="text-blue-500 text-3xl mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <span className="text-slate-300 font-medium">{item.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Report Preview Section */}
      <section id="tu-reporte" className="py-32 px-6 bg-slate-950 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[120px]"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            <motion.div {...fadeIn}>
              <h2 className="text-4xl font-bold text-white mb-6">Tu reporte incluirá</h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                No solo te decimos qué está mal, te damos las herramientas para arreglarlo. 
                Al finalizar el diagnóstico, tendrás acceso a un panel interactivo con:
              </p>
              
              <ul className="space-y-4">
                {[
                  "Puntuación general (0-100)",
                  "Nivel de riesgo detallado",
                  "Análisis de áreas fuertes y débiles",
                  "Recomendaciones específicas por categoría",
                  "Plan de acción prioritario de 30 días",
                  "Opción de descarga en formato PDF profesional"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-300">
                    <SafeIcon icon={FiCheckCircle} className="text-emerald-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              {...fadeIn} 
              transition={{ delay: 0.2 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl relative"
            >
              <div className="flex justify-between items-center mb-8 border-b border-slate-800 pb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div className="text-xs font-mono text-slate-500 tracking-widest uppercase">Preview-Report-01</div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-slate-500 text-xs mb-1">Puntuación General</div>
                    <div className="text-3xl font-black text-white">72 <span className="text-sm font-normal text-slate-500">/ 100</span></div>
                  </div>
                  <div className="px-3 py-1 bg-yellow-500/10 text-yellow-500 rounded-full text-xs font-bold border border-yellow-500/20 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                    Riesgo Medio
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Accesos y MFA</span>
                    <span className="text-blue-400 font-bold">85%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 w-[85%] rounded-full"></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Respaldo y Recuperación</span>
                    <span className="text-red-400 font-bold">42%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500 w-[42%] rounded-full"></div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-800">
                  <div className="flex items-center gap-2 text-blue-400 text-sm font-bold mb-4">
                    <SafeIcon icon={FiBarChart2} />
                    Recomendación prioritaria
                  </div>
                  <div className="bg-slate-950 p-4 rounded-xl text-xs text-slate-400 italic border border-slate-800/50">
                    "Implementar respaldos automáticos fuera de sitio y realizar una prueba de restauración completa antes del 15 de Octubre."
                  </div>
                </div>

                <div className="flex justify-center mt-6">
                  <div className="px-4 py-2 bg-blue-600/10 text-blue-400 rounded-lg text-xs font-bold border border-blue-500/20 flex items-center gap-2">
                    <SafeIcon icon={FiDownload} />
                    Descargar PDF Completo
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">¿Listo para asegurar tu negocio?</h2>
            <p className="text-xl text-slate-400 mb-12">
              No dejes la seguridad al azar. Toma el control hoy mismo con nuestra evaluación profesional gratuita.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => navigate('/assessment')}
                className="w-full sm:w-auto px-10 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xl transition-all shadow-2xl shadow-blue-600/40"
              >
                Comenzar Evaluación Ahora
              </button>
            </div>
            <p className="mt-12 text-xs text-slate-500 italic max-w-2xl mx-auto leading-relaxed">
              * Este diagnóstico tiene fines educativos e informativos. No constituye una auditoría oficial, certificación, asesoría legal ni garantía de cumplimiento. Su propósito es ayudarte a identificar áreas de mejora en la seguridad de tu red.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}